"use strict";
/**
 * Contract for the dashboard's per-employee starter prompts (dashboard-employee-starter-prompts).
 * Shared between the backend generation service and the frontend dock/canvas so both sides agree
 * on employee identity and the cached payload shape.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.STARTER_PROMPT_CAPABILITY_EMPLOYEE_MAP = exports.STARTABLE_CHAT_EMPLOYEE_IDS = void 0;
exports.isStartableChatEmployeeId = isStartableChatEmployeeId;
exports.getEmployeeForCapability = getEmployeeForCapability;
/**
 * Startable chat employee ids eligible for a generated starter prompt. Mirrors
 * `STARTABLE_CHAT_EMPLOYEES` in `apps/web/src/features/agents/utils/agent-chat-employee-registry.ts`
 * (the frontend source of truth for employee identity) — duplicated here because that registry is
 * frontend-only and not importable from shared/backend code. Keep in sync if it changes; a research
 * lead has no `startingModeId` there and is intentionally absent here too.
 */
exports.STARTABLE_CHAT_EMPLOYEE_IDS = [
    'account-manager',
    'graphic-designer',
    'videographer',
    'media-buyer-meta',
    'media-buyer-google',
    'media-buyer-tiktok',
    'social-media-manager',
    'meta-ads-researcher',
];
const STARTABLE_CHAT_EMPLOYEE_ID_SET = new Set(exports.STARTABLE_CHAT_EMPLOYEE_IDS);
/** Whether a string is one of the startable chat employee ids. */
function isStartableChatEmployeeId(value) {
    return STARTABLE_CHAT_EMPLOYEE_ID_SET.has(value);
}
/**
 * Code-owned map from a platform capability id (`PLATFORM_CAPABILITY_MANIFEST` in
 * `apps/api/src/services/home-dashboard/capability-manifest.ts`) to the startable employee whose
 * craft owns it. Used to bucket the home briefing's active chat next steps per employee before
 * starter-prompt generation, so an employee's generation call only ever sees plan steps mapped to
 * it here — the Videographer never sees a static-ads step, etc.
 *
 * Every value is statically constrained to `IStartableChatEmployeeId` (compiler-enforced). A
 * colocated consistency test cannot also assert every key is a real manifest id here: the manifest
 * lives in `apps/api` (backend-only), and `packages/shared` has no dependency on `apps/api` and no
 * test runner of its own — that half of the consistency check lives in
 * `apps/api/src/services/agents/starter-prompts-generation-service.test.ts`, colocated with the
 * backend code that actually imports both sides.
 */
exports.STARTER_PROMPT_CAPABILITY_EMPLOYEE_MAP = {
    'static-ads': 'graphic-designer',
    'video-ads': 'videographer',
    // Research-flavored capabilities all resolve to the Meta Ads Researcher: its mode is the
    // dashboard's winning-ad discovery / competitor-breakdown / niche-pattern-analysis specialist.
    'competitor-research': 'meta-ads-researcher',
    'ad-research': 'meta-ads-researcher',
    'creative-analysis': 'meta-ads-researcher',
    'executive-ad-report': 'meta-ads-researcher',
    // Cross-channel / generalist capabilities fall to the Account Manager (also the router
    // employee, which already receives the full active plan-step list per D3).
    scheduling: 'account-manager',
    'google-analytics': 'account-manager',
    'shopify-analytics': 'account-manager',
    'klaviyo-email': 'account-manager',
    'slack-notifications': 'account-manager',
    // Paid-media management and analytics: each ad platform has its own media buyer.
    'meta-ads-management': 'media-buyer-meta',
    'meta-ads-analysis': 'media-buyer-meta',
    'google-ads-management': 'media-buyer-google',
    'tiktok-ads-management': 'media-buyer-tiktok',
    // Organic posting is the Social Media Manager's craft.
    'tiktok-organic': 'social-media-manager',
    'instagram-organic': 'social-media-manager',
};
/** Resolves the startable employee that owns a capability id, or undefined if unmapped. */
function getEmployeeForCapability(capabilityId) {
    return exports.STARTER_PROMPT_CAPABILITY_EMPLOYEE_MAP[capabilityId];
}
